import React, { memo, useCallback, useEffect, useRef } from "react";
import _noop from "lodash/noop";
import styled from "@emotion/styled";
import JsBarcode from "react-barcode";

// import { DELIVERY_TYPE_NORMAL } from "@/Models/Delivery";

// 마스킹 유틸
const HANGUL_REGEX = /[\uAC00-\uD7A3]/;

/**
 * 성명 마스킹 (공백 제외 글자수 기준)
 * - 2글자: 2번째 자리
 * - 3글자: 2번째 자리
 * - 4글자: 2, 4번째 자리
 * - 5글자 이상
 *    국문   : 2번째 자리 + 4번째 자리 이후 전부
 *    국문 외: 5번째 자리 이후 전부
 */
function maskName(name) {
  if (!name) return name;

  const chars = Array.from(name);
  const nonSpaceIdx = [];
  chars.forEach((c, i) => {
    if (c !== " ") nonSpaceIdx.push(i);
  });
  const n = nonSpaceIdx.length;
  if (n < 2) return name; // 1글자는 마스킹 대상 아님

  const isKorean = HANGUL_REGEX.test(name);
  const maskPositions = new Set();

  if (n === 2 || n === 3) {
    maskPositions.add(2);
  } else if (n === 4) {
    maskPositions.add(2);
    maskPositions.add(4);
  } else {
    if (isKorean) {
      maskPositions.add(2);
      for (let p = 4; p <= n; p++) maskPositions.add(p);
    } else {
      for (let p = 5; p <= n; p++) maskPositions.add(p);
    }
  }

  let count = 0;
  return chars
    .map((c) => {
      if (c === " ") return c; // 공백은 그대로 유지, 카운트에서 제외
      count++;
      return maskPositions.has(count) ? "*" : c;
    })
    .join("");
}

/**
 * 연락처 마스킹 - 마지막 4자리를 **** 로 치환
 * (안심번호 적용 케이스는 delivery.receiverMobile 자체가
 *  안심번호로 내려온다는 전제 하에 동일 로직 적용)
 */
function maskMobile(mobile) {
  if (!mobile) return mobile;
  return mobile.replace(/(\d{4})$/, "****");
}

// 기본주소에 상세(층/호 등)가 붙어 저장된 경우 해당 토큰만 제거한다.
const ADDRESS_DETAIL_TOKEN_REGEX =
  /^(지하\s*)?\d+\s*층$|^[Bb]\d+\s*층$|^제?\d+\s*호$|^[A-Za-z]?\d+동$/;

function stripEmbeddedAddressDetail(address, detail) {
  if (!address) return address;

  let addr = address.trim();
  const extra = (detail || "").trim();

  if (extra && extra !== addr && addr.endsWith(extra)) {
    addr = addr.slice(0, -extra.length).trim();
  }

  const tokens = addr.split(/\s+/).filter(Boolean);
  while (
    tokens.length > 1 &&
    ADDRESS_DETAIL_TOKEN_REGEX.test(tokens[tokens.length - 1])
  ) {
    tokens.pop();
  }

  return tokens.join(" ");
}

const PrintContainer = styled.div``;
const WaybillContainer = styled.div``;

// 135 너비를 150에서 정중앙에 위치 (150 - 123) / 2 = 13.5mm 여백 - 13mm로 고정

/**
 * Style
 */
const NANUM_FONT_STACK = '"Nanum Gothic", sans-serif';

const PrintContainerStyle = {
  padding: 0,
  margin: 0,
  fontFamily: NANUM_FONT_STACK,
};

const HANJIN_LOGO_SRC = `${window.location.origin}${
  process.env.PUBLIC_URL || ""
}/assets/images/hanjin-logo.png`;

const WaybillContainerStyle = {
  position: "relative",
  width: "100mm",
  height: "150mm",
  margin: 0,
  padding: 0,
  overflow: "hidden",
  clipPath: "inset(0)",
  boxSizing: "border-box",
  pageBreakAfter: "always",
  breakAfter: "page",
  pageBreakInside: "avoid",
  breakInside: "avoid",
};

const DefaultStyle = {
  transform: "rotate(-90deg)",
  transformOrigin: "left top",
  position: "absolute",
  fontSize: `11px`,
  whiteSpace: "pre-wrap",
  lineHeight: "1",
  letterSpacing: "-0.8px",
};

const DefaultComponent = ({ children, style = {} }) => {
  return <div style={{ ...DefaultStyle, ...style }}>{children}</div>;
};

const OrderIdFromCorp = memo(({ delivery }) => {
  const orderIdFromCorp = delivery.orderIdFromCorp?.replace(
    /(.{4})(?=.)/g,
    "$1-",
  );

  const style = {
    top: "135mm", // 우측에서의 위치 150 - 13 - 2 = 135mm
    left: "2mm", // 상단 여백
    fontSize: "16px",
    fontWeight: "bold",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      <span style={{ marginRight: "1mm", fontSize: "10px" }}>운송장 번호</span>
      {orderIdFromCorp || delivery.bookId}
    </DefaultComponent>
  );
});

const HanjinLogo = memo(({ onAfterRendered = _noop }) => {
  const imgRef = useRef(null);
  const doneRef = useRef(false);

  const handleDone = useCallback(() => {
    if (doneRef.current) return;
    doneRef.current = true;
    onAfterRendered();
  }, [onAfterRendered]);

  useEffect(() => {
    if (imgRef.current?.complete) {
      handleDone();
    }
  }, [handleDone]);

  const style = {
    top: "47mm", // 우측에서의 위치 150 - 13 - 90 = 47mm
    left: "1mm", // 상단 여백
    fontSize: "12px",
    fontWeight: "500",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      <img
        ref={imgRef}
        alt="한진 로고 이미지"
        src={HANJIN_LOGO_SRC}
        onLoad={handleDone}
        onError={handleDone}
        style={{ width: "30mm", height: "auto", display: "block" }}
      />
      <div
        style={{
          textAlign: "center",
          fontSize: "11px",
          fontWeight: "500",
        }}
      >
        1588-0011
      </div>
    </DefaultComponent>
  );
});

const HubCode = memo(({ delivery }) => {
  const style = {
    top: "133mm", // 우측에서의 위치
    left: "11mm", // 상단 여백
    fontSize: "34px",
    fontWeight: "bold",
    whiteSpace: "pre",
    wordBreak: "break-all",
    letterSpacing: "-2px",
  };

  return (
    <DefaultComponent style={style}>
      {delivery.hanjinPrintWblHubCod} {delivery.hanjinPrintWblTmlCod}{" "}
      {delivery.hanjinPrintWblDomMid}
    </DefaultComponent>
  );
});

const RiderInfo = memo(({ delivery }) => {
  const style = {
    top: "92mm", // 우측에서의 위치
    left: "11mm", // 상단 여백
    fontSize: "24px",
    fontWeight: "bold",
    whiteSpace: "pre",
    wordBreak: "break-all",
    textAlign: "right",
    letterSpacing: "-2px",
  };

  return (
    <DefaultComponent style={style}>
      <span style={{ fontSize: "22px" }}>{delivery.hanjinPrintWblEsCod}</span>
      <br />
      {delivery.hanjinPrintWblEsNam}
    </DefaultComponent>
  );
});

const ReturnCode = memo(({ delivery }) => {
  const style = {
    top: "60mm", // 우측에서의 위치
    left: "11mm", // 상단 여백
    fontSize: "10px",
    fontWeight: "bold",
    whiteSpace: "pre",
    wordBreak: "break-all",
    textAlign: "left",
    letterSpacing: "-2px",
    lineHeight: "1.05",
  };

  return (
    <DefaultComponent style={style}>
      <span style={{ fontSize: "22px" }}>{delivery.hanjinPrintWblGrpRnk}</span>
      <br />
      {delivery.hanjinPrintWblCenCod}
      <br />
      {delivery.hanjinPrintWblCenNam}
    </DefaultComponent>
  );
});

const SenderTerminalCode = memo(({ delivery }) => {
  const style = {
    top: "131mm", // 우측에서의 위치 150 - 13 - 6 = 131mm
    left: "20mm", // 상단 여백
    fontSize: "8px",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      발지:{delivery.hanjinPrintWblStmlCod} {delivery.hanjinPrintWblStmlNam}
    </DefaultComponent>
  );
});

const ReceiverLabel = memo(() => {
  return (
    <DefaultComponent
      style={{
        top: "137mm", // 우측에서의 위치 150 - 13 = 137mm
        left: "23.5mm",
        height: "18.5mm",
        width: "5mm",
        fontSize: "11px",
        fontWeight: "600",
        whiteSpace: "pre",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      받<br />는<br />분
    </DefaultComponent>
  );
});

const ReceiverName = memo(({ delivery }) => {
  const style = {
    top: "130mm", // 우측에서의 위치 150 - 13 - 7 = 130mm
    left: "25mm", // 상단 여백
    fontSize: "11px",
    fontWeight: "bold",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {maskName(delivery.receiverName)}
    </DefaultComponent>
  );
});

const ReceiverMobile = memo(({ delivery }) => {
  const style = {
    top: "108mm", // 우측에서의 위치 137 - 29 = 108mm
    left: "25mm", // 상단 여백
    fontSize: "11px",
    fontWeight: "bold",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {maskMobile(delivery.receiverMobile)}
    </DefaultComponent>
  );
});

const ReceiverAddress = memo(({ delivery }) => {
  const style = {
    top: "130mm", // 우측에서의 위치 150 - 13 - 7 = 130mm
    left: "28mm", // 상단 여백
    fontSize: "14px",
    fontWeight: "bold",
    whiteSpace: "pre",
  };

  const baseAddress = stripEmbeddedAddressDetail(
    delivery.receiverAddressRoad || delivery.receiverAddress,
    delivery.receiverAddressDetail,
  );

  return (
    <DefaultComponent style={style}>
      {baseAddress} {baseAddress && "****"}
    </DefaultComponent>
  );
});

const ReceiverAddressWithoutSigungu = memo(({ delivery }) => {
  const style = {
    top: "130mm", // 우측에서의 위치 150 - 13 - 7 = 130mm
    left: "35mm", // 상단 여백
    fontSize: "18px",
    fontWeight: "bold",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {delivery.hanjinPrintWblPrtAdd}
    </DefaultComponent>
  );
});

const Price = memo(() => {
  const style = {
    top: "49mm", // 우측에서의 위치 137 - 88 = 49mm
    left: "35.5mm", // 상단 여백
    fontSize: "14px",
    fontWeight: "bold",
    whiteSpace: "pre",
    width: "32mm",
    textAlign: "center",
    border: "0.5mm solid black",
    padding: "1mm 0",
  };

  return <DefaultComponent style={style}>신용</DefaultComponent>;
});

const SenderLabel = memo(() => {
  return (
    <DefaultComponent
      style={{
        top: "137mm", // 우측에서의 위치 150 - 13 = 137mm
        left: "42mm",
        height: "10mm",
        width: "5mm",
        fontSize: "11px",
        fontWeight: "600",
        whiteSpace: "pre",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      보<br />낸<br />분
    </DefaultComponent>
  );
});

const SenderName = memo(({ delivery }) => {
  const style = {
    top: "130mm", // 우측에서의 위치 150 - 13 - 7 = 130mm
    left: "44mm", // 상단 여백
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {maskName(delivery.senderName)}
    </DefaultComponent>
  );
});

const SenderMobile = memo(({ delivery }) => {
  const style = {
    top: "77mm", // 우측에서의 위치 150 - 13 - 60 = 77mm
    left: "44mm", // 상단 여백
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {maskMobile(delivery.senderMobile)}
    </DefaultComponent>
  );
});

const DateAndType = memo(({ delivery }) => {
  const style = {
    top: "43mm", // 우측에서의 위치 150 - 13 - 94 = 43mm
    left: "44mm", // 상단 여백
    fontSize: "11px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {delivery.renderReceiptDate("yyyy.MM.dd.")}
    </DefaultComponent>
  );
});

const SenderAddress = memo(({ delivery }) => {
  const style = {
    top: "130mm", // 우측에서의 위치 150 - 13 - 7 = 130mm
    left: "48mm", // 상단 여백
    fontSize: "12px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {delivery.senderAddressRoad || delivery.senderAddress}{" "}
      {(delivery.senderAddressRoad || delivery.senderAddress) && "****"}
    </DefaultComponent>
  );
});

const ProductNameAndOthers = memo(({ delivery }) => {
  const style = {
    top: "135mm", // 우측에서의 위치 150 - 13 - 2 = 135mm
    left: "54mm", // 상단 여백
    fontSize: "12px",
    fontWeight: "600",
    whiteSpace: "pre",
    lineHeight: "1.2",
  };

  return (
    <DefaultComponent style={style}>
      상품명: {delivery.productName}
      <br />
      원송장번호: {delivery.etc2 || ""}
      <br />
      수거지시번호: {delivery.orderIdFromCorpSub}
    </DefaultComponent>
  );
});

const Notice = memo(() => {
  const style = {
    top: "133mm", // 우측에서의 위치 150 - 13 - 4 = 133mm
    left: "82mm", // 상단 여백
    fontSize: "9px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      ※개인정보 보호를 위하여 인수하신 운송장을 폐기하여 주시기 바랍니다.
    </DefaultComponent>
  );
});

const Memo = memo(({ delivery }) => {
  const style = {
    top: "133mm", // 우측에서의 위치 150 - 13 - 4 = 133mm
    left: "92mm", // 상단 여백
    fontSize: "12px",
    fontWeight: "600",
    whiteSpace: "pre",
  };

  return (
    <DefaultComponent style={style}>
      {delivery.memoFromCustomer}
    </DefaultComponent>
  );
});

// const ProductCount = ({ delivery }) => {
//   const style = {
//     top: "36mm", // 우측에서의 위치 150 - 13 - 101 = 36mm
//     left: "74mm", // 상단 여백
//     fontSize: "10px",
//     whiteSpace: "pre",
//     letterSpacing: "-0.3px",
//   };

//   return <DefaultComponent style={style}>1/0 (건수/수량)</DefaultComponent>;
// };

const Barcode = ({ delivery }) => {
  const orderIdFromCorp = delivery.orderIdFromCorp || delivery.bookId;

  return (
    <DefaultComponent
      style={{
        top: "61mm", // 우측에서의 위치 150 - 13 - 76 = 61mm
        left: "78mm",
        width: "42mm",
        height: "15mm",
      }}
    >
      <JsBarcode
        margin={0}
        width={
          orderIdFromCorp.length > 16
            ? 1
            : orderIdFromCorp.length > 12
              ? 1.1
              : 1.3
        }
        height={55}
        value={orderIdFromCorp}
        style={{ width: "100%", height: "100%" }}
        displayValue={false}
      />
    </DefaultComponent>
  );
};

const BarcodeText = ({ delivery }) => {
  const orderIdFromCorp = delivery.orderIdFromCorp?.replace(
    /(.{4})(?=.)/g,
    "$1-",
  );

  return (
    <DefaultComponent
      style={{
        top: "61mm", // 우측에서의 위치 150 - 13 - 76 = 61mm
        left: "94mm",
        width: "42mm",
        textAlign: "center",
        fontSize: "11px",
      }}
    >
      {orderIdFromCorp || delivery.bookId}
    </DefaultComponent>
  );
};

const BorderGrid = memo(() => {
  return (
    <DefaultComponent
      style={{
        top: "137mm",
        left: "0mm",
        width: "123mm",
        height: "100mm",
        boxSizing: "border-box",
      }}
    >
      <svg
        width="123mm"
        height="100mm"
        viewBox="0 0 123 100"
        style={{ display: "block" }}
      >
        <rect
          x="0.25"
          y="0.25"
          width="122.5"
          height="99.5"
          fill="none"
          stroke="#000"
          strokeWidth="0.5"
        />
        <line x1="0" y1="10" x2="123" y2="10" stroke="#000" strokeWidth="0.5" />
        <line
          x1="0"
          y1="23.5"
          x2="123"
          y2="23.5"
          stroke="#000"
          strokeWidth="0.5"
        />
        <line x1="0" y1="42" x2="123" y2="42" stroke="#000" strokeWidth="0.5" />
        <line x1="0" y1="52" x2="123" y2="52" stroke="#000" strokeWidth="0.5" />
        <line x1="5" y1="23.5" x2="5" y2="42" stroke="#000" strokeWidth="0.5" />
        <line x1="5" y1="42" x2="5" y2="52" stroke="#000" strokeWidth="0.5" />
      </svg>
    </DefaultComponent>
  );
});

const WaybillWrap = memo(
  ({ delivery, isLast = false, handleAfterRendered = _noop }) => {
    return (
      <>
        <WaybillContainer
          className="label"
          style={{
            ...WaybillContainerStyle,
            ...(isLast ? { pageBreakAfter: "auto", breakAfter: "auto" } : {}),
          }}
        >
          <BorderGrid />
          {/* 상단 영역 */}
          <>
            <OrderIdFromCorp delivery={delivery} />
            <HanjinLogo onAfterRendered={handleAfterRendered} />
          </>
          <>
            <HubCode delivery={delivery} />
            <RiderInfo delivery={delivery} />
            <ReturnCode delivery={delivery} />
            <SenderTerminalCode delivery={delivery} />
          </>
          {/* 수하인 정보 */}
          <>
            <ReceiverLabel />
            <ReceiverName delivery={delivery} />
            <ReceiverMobile delivery={delivery} />
            <ReceiverAddress delivery={delivery} />
            <ReceiverAddressWithoutSigungu delivery={delivery} />
            <Price delivery={delivery} />
          </>
          {/* 송하인 정보 */}
          <>
            <SenderLabel />
            <SenderName delivery={delivery} />
            <SenderMobile delivery={delivery} />
            <DateAndType delivery={delivery} />
            <SenderAddress delivery={delivery} />
          </>
          <ProductNameAndOthers delivery={delivery} />
          <Notice />
          <Memo delivery={delivery} />
          <>
            {/* <ProductCount delivery={delivery} /> */}
            <Barcode delivery={delivery} />
            <BarcodeText delivery={delivery} />
          </>
        </WaybillContainer>
      </>
    );
  },
);

const NANUM_FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Nanum+Gothic:wght@400;700;800&display=swap";

// 출력 창(doc)의 head 에 나눔고딕 스타일시트를 주입하고,
// 스타일시트 로드가 끝나면 resolve 되는 Promise 를 반환한다.
const injectNanumFont = (doc) => {
  return new Promise((resolve) => {
    if (!doc) {
      resolve();
      return;
    }

    const existing = doc.getElementById("nanum-gothic-font");
    if (existing) {
      if (existing.dataset.loaded === "true") {
        resolve();
      } else {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => resolve(), { once: true });
      }
      return;
    }

    const preconnect1 = doc.createElement("link");
    preconnect1.rel = "preconnect";
    preconnect1.href = "https://fonts.googleapis.com";

    const preconnect2 = doc.createElement("link");
    preconnect2.rel = "preconnect";
    preconnect2.href = "https://fonts.gstatic.com";
    preconnect2.crossOrigin = "anonymous";

    const stylesheet = doc.createElement("link");
    stylesheet.id = "nanum-gothic-font";
    stylesheet.rel = "stylesheet";
    stylesheet.href = NANUM_FONT_HREF;
    stylesheet.addEventListener(
      "load",
      () => {
        stylesheet.dataset.loaded = "true";
        resolve();
      },
      { once: true },
    );
    stylesheet.addEventListener("error", () => resolve(), { once: true });

    doc.head.appendChild(preconnect1);
    doc.head.appendChild(preconnect2);
    doc.head.appendChild(stylesheet);
  });
};

// 스타일시트 로드 -> 실제 폰트 페이스 로드 -> 전체 폰트 준비 완료 순서로
// 기다려, 인쇄 시점에 나눔고딕이 확실히 적용되도록 한다.
const waitForNanumFont = async (doc) => {
  if (!doc) return;

  await injectNanumFont(doc);

  if (!doc.fonts) return;

  try {
    await Promise.all([
      doc.fonts.load('400 12px "Nanum Gothic"'),
      doc.fonts.load('700 12px "Nanum Gothic"'),
      doc.fonts.load('800 12px "Nanum Gothic"'),
    ]);
    await doc.fonts.ready;
  } catch (e) {
    // 폰트 로딩 실패 시에도 인쇄는 진행한다.
  }
};

export default function Print100150V3Component({
  deliveries = [],
  onLoaded = _noop,
}) {
  const loadedCount = useRef(0);
  const containerRef = useRef(null);

  useEffect(() => {
    injectNanumFont(containerRef.current?.ownerDocument);
  }, []);

  const handleAfterRendered = useCallback(async () => {
    loadedCount.current++;
    if (loadedCount.current === deliveries.length) {
      await waitForNanumFont(containerRef.current?.ownerDocument);
      onLoaded();
    }
  }, [deliveries.length, onLoaded]);

  return (
    <PrintContainer ref={containerRef} style={PrintContainerStyle}>
      {deliveries.map((d, index) => {
        return (
          <WaybillWrap
            key={index}
            delivery={d}
            isLast={index === deliveries.length - 1}
            handleAfterRendered={handleAfterRendered}
          />
        );
      })}
    </PrintContainer>
  );
}
